import type { Coordinate, FuelCostByDay, FuelCosts, MaintenanceCosts, MaintenanceCostsByDay, PageableContent, PageableContentAPI } from '@cobliteam/common-types';
import type { LatLng } from '../geocode';
import { parseCategory } from '../geofence/GeofenceTransformer';
import type {
  ApiFuelCosts,
  ApiMaintenanceCosts,
  RouteStep,
  RouteStepApi,
  StopRouteStep,
  StopRouteStepApi,
  TripRouteStep,
  TripRouteStepApi,
  VehicleFields,
  VehicleFieldsApi,
  VehicleRoute,
  VehicleRouteApi,
} from './Types';

export const toFuelCosts = (apiFuelCosts: ApiFuelCosts): FuelCosts => {
  const costs: { [id: string]: FuelCostByDay[] } = {};
  for (const vehicleId of Object.keys(apiFuelCosts)) {
    costs[vehicleId] = apiFuelCosts[vehicleId]
      .filter((dayOfFuel) => dayOfFuel.cost !== 0)
      .map((dayOfFuel) => ({
        dateInMillis: dayOfFuel.begin_day,
        cost: dayOfFuel.cost,
        driversId: dayOfFuel.driver_ids,
        distanceDrivenInMeters: dayOfFuel.distance_driven_in_meters,
      }));
  }

  return costs;
};

export const toMaintenanceCosts = (apiMaintenanceCosts: ApiMaintenanceCosts): MaintenanceCosts => {
  const costs: { [id: string]: MaintenanceCostsByDay[] } = {};
  Object.keys(apiMaintenanceCosts).forEach((vehicleId) => {
    costs[vehicleId] = apiMaintenanceCosts[vehicleId].map((dayOfFuel) => ({
      dateInMillis: dayOfFuel.begin_day,
      corrective: dayOfFuel.corrective,
      preventive: dayOfFuel.preventive,
    }));
  });

  return costs;
};

const toVehicleFields = (vehicleFieldsApi: VehicleFieldsApi): VehicleFields => ({
  id: vehicleFieldsApi.id,
  licensePlate: vehicleFieldsApi.license_plate,
  model: vehicleFieldsApi.model,
  brand: vehicleFieldsApi.brand,
  alias: vehicleFieldsApi.alias,
});

const toRouteStep = (routeStepApi: RouteStepApi): RouteStep => {
  const commonFields = {
    type: routeStepApi.type,
    startTime: routeStepApi.start_time,
    endTime: routeStepApi.end_time,
    driverId: routeStepApi.driver_id,
    cost: routeStepApi.cost,
  };

  const toCoordinate = ({ lat, lng }: LatLng): Coordinate => ({
    latitude: lat,
    longitude: lng,
  });

  const toStopRouteStep = (stepApi: StopRouteStepApi): StopRouteStep => ({
    ...commonFields,
    deviceId: stepApi.device_id,
    latitude: stepApi.latitude,
    longitude: stepApi.longitude,
    idleOnInMillis: stepApi.idle_on_in_millis,
    idleOffInMillis: stepApi.idle_off_in_millis,
    hasFilteredIdleOnTime: stepApi.has_filtered_idle_on_time,
    geofences: stepApi.geofences.map((geoApi) => ({
      id: geoApi.id,
      name: geoApi.name,
      address: geoApi.address,
      centerLatLong: toCoordinate(geoApi.center),
      radius: geoApi.radius,
      createdAt: geoApi.created_at,
      category: parseCategory(geoApi.category),
    })),
  });

  const toTripRouteStep = (stepApi: TripRouteStepApi): TripRouteStep => ({
    ...commonFields,
    tripDistance: stepApi.trip_distance,
    cost: stepApi.cost,
  });

  if (routeStepApi.type === 'stop') {
    return toStopRouteStep(routeStepApi as StopRouteStepApi);
  }
  return toTripRouteStep(routeStepApi as TripRouteStepApi);
};

export const toVehicleRoute = (vehicleRoutesApi: VehicleRouteApi): VehicleRoute => ({
  vehicle: toVehicleFields(vehicleRoutesApi.vehicle),
  routeSteps: vehicleRoutesApi.route_steps.map(toRouteStep),
  incidents: vehicleRoutesApi.incidents,
});

export const toVehicleRoutesPage = (pagedVehicleRoutesApi: PageableContentAPI<VehicleRouteApi>): PageableContent<VehicleRoute> => {
  const vehicleRoutesApi = pagedVehicleRoutesApi.content;

  const vehicleRoutes: VehicleRoute[] = vehicleRoutesApi.map(toVehicleRoute);

  return {
    content: vehicleRoutes,
    last: pagedVehicleRoutesApi.last,
    totalPages: pagedVehicleRoutesApi.total_pages,
    totalElements: pagedVehicleRoutesApi.total_elements,
    numberOfElements: pagedVehicleRoutesApi.number_of_elements,
    number: pagedVehicleRoutesApi.number,
    size: pagedVehicleRoutesApi.size,
    first: pagedVehicleRoutesApi.first,
    empty: pagedVehicleRoutesApi.empty,
  };
};
